import type { StateCreator } from 'zustand';
import type { IssueLinkedDocumentationSlice } from './types';
import {
  setBoundedCachePair,
  setBoundedMetaEntry,
  stripDocumentationModelForCache,
  pickHashMapEntries,
  withPendingRemovalId,
  withoutPendingRemovalId,
} from './model';
import { ResourceLoadStatus } from 'shared/domain/issue/linkedDocumentation';
import { LINKED_DOCUMENTATION_CACHE_POLICY } from 'shared/domain/issue/linkedDocumentation';

type IssueLinkedDocumentationSliceCreator = StateCreator<
  IssueLinkedDocumentationSlice,
  [],
  [],
  IssueLinkedDocumentationSlice
>;

export const createIssueLinkedDocumentationSlice: IssueLinkedDocumentationSliceCreator =
  (set) => ({
    linkedDocumentationItems: [],
    linkedDocumentationActiveIssueId: undefined,
    pendingRemovalLinkedDocumentationIds: {},
    linkedDocumentationPreviewsByVersionId: {},
    linkedDocumentationPreviewMetaByVersionId: {},
    linkedDocumentationDetailsByDocumentationId: {},
    linkedDocumentationDetailsMetaByDocumentationId: {},
    setLinkedDocumentationItems: ({ issueId, items }) =>
      set((state: IssueLinkedDocumentationSlice) => {
        const isNewIssue = issueId !== state.linkedDocumentationActiveIssueId;

        if (!isNewIssue) {
          return {
            linkedDocumentationActiveIssueId: issueId,
            linkedDocumentationItems: items,
          };
        }

        const versionIds = new Set(
          items
            .map((item) =>
              item.versionId != null ? String(item.versionId) : undefined
            )
            .filter((value): value is string => Boolean(value))
        );
        const documentationIds = new Set(
          items
            .map((item) =>
              item.documentationId != null
                ? String(item.documentationId)
                : undefined
            )
            .filter((value): value is string => Boolean(value))
        );

        return {
          linkedDocumentationActiveIssueId: issueId,
          linkedDocumentationItems: items,
          pendingRemovalLinkedDocumentationIds: {},
          linkedDocumentationPreviewsByVersionId: pickHashMapEntries(
            state.linkedDocumentationPreviewsByVersionId,
            versionIds
          ),
          linkedDocumentationPreviewMetaByVersionId: pickHashMapEntries(
            state.linkedDocumentationPreviewMetaByVersionId,
            versionIds
          ),
          linkedDocumentationDetailsByDocumentationId: pickHashMapEntries(
            state.linkedDocumentationDetailsByDocumentationId,
            documentationIds
          ),
          linkedDocumentationDetailsMetaByDocumentationId: pickHashMapEntries(
            state.linkedDocumentationDetailsMetaByDocumentationId,
            documentationIds
          ),
        };
      }),
    addPendingRemoval: (linkedDocumentationId) =>
      set((state: IssueLinkedDocumentationSlice) => ({
        pendingRemovalLinkedDocumentationIds: withPendingRemovalId(
          state.pendingRemovalLinkedDocumentationIds,
          linkedDocumentationId
        ),
      })),
    removePendingRemoval: (linkedDocumentationId) =>
      set((state: IssueLinkedDocumentationSlice) => {
        if (!state.pendingRemovalLinkedDocumentationIds[linkedDocumentationId]) {
          return state;
        }

        return {
          pendingRemovalLinkedDocumentationIds: withoutPendingRemovalId(
            state.pendingRemovalLinkedDocumentationIds,
            linkedDocumentationId
          ),
        };
      }),
    startLinkedDocumentationPreviewRequest: (versionId) =>
      set((state: IssueLinkedDocumentationSlice) => {
        const previousMeta =
          state.linkedDocumentationPreviewMetaByVersionId[versionId];

        return {
          linkedDocumentationPreviewMetaByVersionId: setBoundedMetaEntry(
            state.linkedDocumentationPreviewMetaByVersionId,
            versionId,
            {
              status: ResourceLoadStatus.Loading,
              requestedAt: Date.now(),
              retryCount: previousMeta?.retryCount ?? 0,
              fetchedAt: previousMeta?.fetchedAt,
              expiresAt: previousMeta?.expiresAt,
            },
            LINKED_DOCUMENTATION_CACHE_POLICY.previewMaxEntries
          ),
        };
      }),
    resolveLinkedDocumentationPreview: (versionId, preview, expiresAt) =>
      set((state: IssueLinkedDocumentationSlice) => {
        const bounded = setBoundedCachePair(
          state.linkedDocumentationPreviewsByVersionId,
          state.linkedDocumentationPreviewMetaByVersionId,
          versionId,
          preview,
          {
            status: ResourceLoadStatus.Ready,
            fetchedAt: Date.now(),
            expiresAt,
            retryCount: 0,
          },
          LINKED_DOCUMENTATION_CACHE_POLICY.previewMaxEntries
        );

        return {
          linkedDocumentationPreviewsByVersionId: bounded.values,
          linkedDocumentationPreviewMetaByVersionId: bounded.metas,
        };
      }),
    failLinkedDocumentationPreview: (versionId, error, nextRetryAt) =>
      set((state: IssueLinkedDocumentationSlice) => ({
        linkedDocumentationPreviewMetaByVersionId: setBoundedMetaEntry(
          state.linkedDocumentationPreviewMetaByVersionId,
          versionId,
          {
            status: ResourceLoadStatus.Error,
            fetchedAt: Date.now(),
            nextRetryAt,
            retryCount:
              (state.linkedDocumentationPreviewMetaByVersionId[versionId]
                ?.retryCount ?? 0) + 1,
            lastError: error,
          },
          LINKED_DOCUMENTATION_CACHE_POLICY.previewMaxEntries
        ),
      })),
    startLinkedDocumentationDetailsRequest: (documentationId) =>
      set((state: IssueLinkedDocumentationSlice) => {
        const previousMeta =
          state.linkedDocumentationDetailsMetaByDocumentationId[
            documentationId
          ];
        const isSilentRefresh =
          previousMeta?.status === ResourceLoadStatus.Ready;

        return {
          linkedDocumentationDetailsMetaByDocumentationId: setBoundedMetaEntry(
            state.linkedDocumentationDetailsMetaByDocumentationId,
            documentationId,
            {
              status: isSilentRefresh
                ? ResourceLoadStatus.Ready
                : ResourceLoadStatus.Loading,
              requestedAt: Date.now(),
              retryCount: previousMeta?.retryCount ?? 0,
              fetchedAt: previousMeta?.fetchedAt,
              expiresAt: previousMeta?.expiresAt,
            },
            LINKED_DOCUMENTATION_CACHE_POLICY.detailsMaxEntries
          ),
        };
      }),
    resolveLinkedDocumentationDetails: (documentationId, details) =>
      set((state: IssueLinkedDocumentationSlice) => {
        const bounded = setBoundedCachePair(
          state.linkedDocumentationDetailsByDocumentationId,
          state.linkedDocumentationDetailsMetaByDocumentationId,
          documentationId,
          stripDocumentationModelForCache(details),
          {
            status: ResourceLoadStatus.Ready,
            fetchedAt: Date.now(),
            expiresAt:
              Date.now() + LINKED_DOCUMENTATION_CACHE_POLICY.detailsTtlMs,
            retryCount: 0,
          },
          LINKED_DOCUMENTATION_CACHE_POLICY.detailsMaxEntries
        );

        return {
          linkedDocumentationDetailsByDocumentationId: bounded.values,
          linkedDocumentationDetailsMetaByDocumentationId: bounded.metas,
        };
      }),
    failLinkedDocumentationDetails: (documentationId, error, nextRetryAt) =>
      set((state: IssueLinkedDocumentationSlice) => ({
        linkedDocumentationDetailsMetaByDocumentationId: setBoundedMetaEntry(
          state.linkedDocumentationDetailsMetaByDocumentationId,
          documentationId,
          {
            status: ResourceLoadStatus.Error,
            fetchedAt: Date.now(),
            nextRetryAt,
            retryCount:
              (state.linkedDocumentationDetailsMetaByDocumentationId[
                documentationId
              ]?.retryCount ?? 0) + 1,
            lastError: error,
          },
          LINKED_DOCUMENTATION_CACHE_POLICY.detailsMaxEntries
        ),
      })),
    resetLinkedDocumentationPreview: (versionId) =>
      set((state: IssueLinkedDocumentationSlice) => {
        const previews = { ...state.linkedDocumentationPreviewsByVersionId };
        delete previews[versionId];

        return {
          linkedDocumentationPreviewsByVersionId: previews,
          linkedDocumentationPreviewMetaByVersionId: {
            ...state.linkedDocumentationPreviewMetaByVersionId,
            [versionId]: {
              status: ResourceLoadStatus.Idle,
              retryCount: 0,
            },
          },
        };
      }),
    resetLinkedDocumentationDetails: (documentationId) =>
      set((state: IssueLinkedDocumentationSlice) => {
        const details = {
          ...state.linkedDocumentationDetailsByDocumentationId,
        };
        delete details[documentationId];

        return {
          linkedDocumentationDetailsByDocumentationId: details,
          linkedDocumentationDetailsMetaByDocumentationId: {
            ...state.linkedDocumentationDetailsMetaByDocumentationId,
            [documentationId]: {
              status: ResourceLoadStatus.Idle,
              retryCount: 0,
            },
          },
        };
      }),
    resetLinkedDocumentation: () =>
      set({
        linkedDocumentationItems: [],
        linkedDocumentationActiveIssueId: undefined,
        pendingRemovalLinkedDocumentationIds: {},
        linkedDocumentationPreviewsByVersionId: {},
        linkedDocumentationPreviewMetaByVersionId: {},
        linkedDocumentationDetailsByDocumentationId: {},
        linkedDocumentationDetailsMetaByDocumentationId: {},
      }),
  });

export type { IssueLinkedDocumentationSlice } from './types';
